import { InfoGrid } from '@/components/ui/info-grid'
import {
    ChevronDown,
    ChevronUp,
    Copy,
    Download,
    Eye,
    FileText,
    Pencil,
    RefreshCw,
    Send,
    TriangleAlert,
} from 'lucide-react'
import { ReactNode, useState } from 'react'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { Button, Tooltip } from 'vnetwork-ui'
import { E_INVOICE_PUBLISHABLE_STATUSES } from '../../constants'
import {
    useEInvoice,
    useEInvoiceErrorText,
    useEInvoiceItems,
} from '../../hooks/use-e-invoice'
import { useEInvoiceAccess } from '../../hooks/use-e-invoice-access'
import { EInvoice } from '../../types'
import {
    E_INVOICE_BADGE_STYLES,
    formatEInvoiceAmount,
    formatEInvoiceDate,
    formatEInvoiceTime,
    formatExchangeRate,
    getEInvoiceBadge,
} from '../../utils/e-invoice'
import EditEInvoiceItemsDialog from './EditEInvoiceItemsDialog'
import EInvoicePdfDialog from './EInvoicePdfDialog'
import MarkEInvoiceWrongDialog from './MarkEInvoiceWrongDialog'
import PublishEInvoiceDialog from './PublishEInvoiceDialog'

interface EInvoiceSectionProps {
    invoiceID: number
    /** invoice_status của hoá đơn DC */
    invoiceStatus?: string
    /** invoice_total của hoá đơn DC: 0 đồng thì không cần xuất hoá đơn điện tử */
    invoiceTotal?: number
    /** CS đã xác nhận hoá đơn cần xuất hoá đơn điện tử (invoices.invoicable) */
    invoicable?: boolean | null
}

const orDash = (value?: ReactNode) =>
    value === undefined || value === null || value === '' ? '—' : value

/**
 * Khối "Hoá đơn điện tử" (MISA meInvoice) trên chi tiết hoá đơn: trạng thái phát hành + cấp mã
 * CQT, thông tin hoá đơn đã phát hành, Xem / Tải PDF / XML, Phát hành hoá đơn, Xác nhận hoá đơn sai,
 * lịch sử phát hành lỗi. Quyền invoices:approved do backend chặn (FE chỉ check trạng thái hoá đơn).
 */
const EInvoiceSection = ({
    invoiceID,
    invoiceStatus,
    invoiceTotal,
    invoicable,
}: EInvoiceSectionProps) => {
    const { t } = useTranslation('invoice')
    const errorText = useEInvoiceErrorText()
    const {
        data: eInvoice,
        isLoading,
        isFetching,
        isError,
        error,
        refetch,
    } = useEInvoice(invoiceID)
    const { data: lines } = useEInvoiceItems(invoiceID)
    const [publishOpen, setPublishOpen] = useState(false)
    const [editItemsOpen, setEditItemsOpen] = useState(false)
    const [markWrongOpen, setMarkWrongOpen] = useState(false)
    const [historyOpen, setHistoryOpen] = useState(false)
    const [pdfOpen, setPdfOpen] = useState(false)

    const badge = getEInvoiceBadge(eInvoice)
    const isPublishableInvoice =
        !!invoiceStatus &&
        E_INVOICE_PUBLISHABLE_STATUSES.includes(invoiceStatus)
    // Lý do khoá nút Phát hành (hiện tooltip), để kế toán không bấm vào rồi mới thấy lỗi
    const publishBlockedReason = !isPublishableInvoice
        ? t('e_invoice_errors.e-invoice-invoice-not-approved')
        : !invoicable
          ? t('e_invoice_errors.e-invoice-invoice-not-invoicable')
          : invoiceTotal === 0
            ? t('e-invoice-zero-total')
            : ''
    // Nhóm được xem nhưng thiếu invoices:approved: chỉ xem trạng thái, ẩn nút thao tác
    const { canManage } = useEInvoiceAccess()
    // Chưa phát hành, hoặc lần gần nhất lỗi → cho phát hành (lại)
    const canPublish = canManage && (!eInvoice || eInvoice.status === 'failed')
    const canMarkWrong =
        canManage &&
        eInvoice?.status === 'published' &&
        !eInvoice.marked_wrong_at
    const isUSD = eInvoice?.currency === 'USD'

    // Link PDF / XML hết hạn sau 1 giờ: luôn GET lại lấy link mới rồi mới mở
    const withFreshFile = async (
        pick: (value: EInvoice) => string | undefined
    ) => {
        const { data } = await refetch()
        return data ? pick(data) : undefined
    }
    const downloadFile = async (
        pick: (value: EInvoice) => string | undefined
    ) => {
        // Link tải có tên file sẵn (hoa-don-<ký hiệu>-<số>.pdf|xml)
        const url = await withFreshFile(pick)
        if (url) window.location.assign(url)
    }

    const copyLookupCode = async (code: string) => {
        try {
            await navigator.clipboard.writeText(code)
            toast.success(t('e-invoice-copied'))
        } catch {
            toast.error(t('e-invoice-copy-failed'))
        }
    }

    const statusValue = (
        <div className="space-y-1">
            <span
                className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${E_INVOICE_BADGE_STYLES[badge]}`}
            >
                {t(`e-invoice-badge-${badge}`)}
            </span>
            {badge === 'failed' && eInvoice?.error_message && (
                <div className="text-xs text-red-700">
                    {t('e-invoice-publish-failed', {
                        message: eInvoice.error_message,
                        code: eInvoice.error_code || '—',
                    })}
                </div>
            )}
        </div>
    )

    const lineItems = lines?.items ?? []
    // Nội dung | số tiền từng dòng sẽ in trên hoá đơn điện tử, để xem trước khi bấm phát hành
    const linesValue = (
        <div className="overflow-hidden rounded-md border border-gray-200">
            <table className="w-full text-sm">
                <thead className="bg-gray-50 text-left text-xs text-gray-500">
                    <tr>
                        <th className="w-10 px-3 py-2 font-medium">#</th>
                        <th className="px-3 py-2 font-medium">
                            {t('e-invoice-item-content')}
                        </th>
                        <th className="w-44 px-3 py-2 text-right font-medium">
                            {t('e-invoice-item-amount')}
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                    {lineItems.map((line, index) => (
                        <tr key={line.invoice_item_id}>
                            <td className="px-3 py-2 align-top text-gray-500">
                                {index + 1}
                            </td>
                            <td className="break-words px-3 py-2 align-top">
                                {line.description}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-right align-top">
                                {formatEInvoiceAmount(
                                    line.amount,
                                    lines?.currency
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    )

    // Hợp đồng / phụ lục liên kết với các dịch vụ trong hoá đơn (gộp trùng giữa các dòng)
    const linkedContracts = [
        ...new Map(
            lineItems
                .flatMap((line) => line.contracts ?? [])
                .map((contract) => [contract.number, contract])
        ).values(),
    ]
    const contractsValue = linkedContracts.length ? (
        <div className="space-y-1">
            {linkedContracts.map((contract) => (
                <div key={contract.number}>
                    <span className="text-gray-500">
                        {t(`e-invoice-item-contract-${contract.kind}`)}:
                    </span>{' '}
                    <span className="font-medium">{contract.number}</span>
                    {contract.contract_number &&
                        ` (${t('e-invoice-item-contract-contract')} ${contract.contract_number})`}
                    {contract.signed_date &&
                        ` · ${t('e-invoice-contract-signed', {
                            date: formatEInvoiceDate(contract.signed_date),
                        })}`}
                </div>
            ))}
        </div>
    ) : (
        <span className="text-gray-500">{t('e-invoice-contracts-none')}</span>
    )

    const items = [
        { label: t('e-invoice-status'), value: statusValue },
        {
            hidden: !eInvoice?.inv_no && !eInvoice?.inv_series,
            label: t('e-invoice-series-number'),
            value: [eInvoice?.inv_series, eInvoice?.inv_no]
                .filter(Boolean)
                .join(' – '),
        },
        {
            hidden: !eInvoice?.inv_date,
            label: t('e-invoice-date'),
            value: formatEInvoiceDate(eInvoice?.inv_date),
        },
        {
            hidden: !eInvoice?.transaction_id,
            label: t('e-invoice-lookup-code'),
            value: eInvoice?.transaction_id && (
                <span className="inline-flex items-center gap-1.5">
                    <a
                        href="https://www.meinvoice.vn/tra-cuu"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-primary hover:underline"
                        title={t('e-invoice-lookup-hint')}
                    >
                        {eInvoice.transaction_id}
                    </a>
                    <button
                        type="button"
                        aria-label={t('e-invoice-copy')}
                        title={t('e-invoice-copy')}
                        className="text-gray-400 hover:text-gray-700"
                        onClick={() =>
                            copyLookupCode(eInvoice.transaction_id ?? '')
                        }
                    >
                        <Copy className="h-3.5 w-3.5" />
                    </button>
                </span>
            ),
        },
        {
            hidden: !eInvoice?.tax_authority_code,
            label: t('e-invoice-tax-authority-code'),
            value: (
                <span className="font-mono text-xs">
                    {eInvoice?.tax_authority_code}
                </span>
            ),
        },
        {
            hidden: eInvoice?.total_amount === undefined,
            label: t('e-invoice-total-amount'),
            value: (
                <div>
                    {formatEInvoiceAmount(
                        eInvoice?.total_amount,
                        eInvoice?.currency
                    )}
                    {isUSD && (
                        <div className="text-xs text-gray-500">
                            {t('e-invoice-exchange-rate')}:{' '}
                            {formatExchangeRate(eInvoice?.exchange_rate)}
                        </div>
                    )}
                </div>
            ),
        },
        {
            hidden: !eInvoice?.buyer_name,
            label: t('e-invoice-buyer'),
            value: (
                <div>
                    {eInvoice?.buyer_name}
                    {eInvoice?.buyer_tax_code && (
                        <div className="text-xs text-gray-500">
                            {t('e-invoice-buyer-tax-code')}:{' '}
                            {eInvoice.buyer_tax_code}
                        </div>
                    )}
                </div>
            ),
        },
        {
            hidden: !eInvoice || eInvoice.status !== 'published',
            label: t('e-invoice-sent-to'),
            value: eInvoice?.receiver_emails?.length
                ? eInvoice.receiver_emails.join(', ')
                : t('e-invoice-no-receiver'),
        },
        {
            hidden: !eInvoice?.published_at && !eInvoice?.published_by_name,
            label: t('e-invoice-published-by'),
            value: `${orDash(eInvoice?.published_by_name)} · ${formatEInvoiceTime(
                eInvoice?.published_at
            )}`,
        },
        {
            hidden: !eInvoice?.marked_wrong_at,
            label: t('e-invoice-marked-wrong'),
            className: 'md:col-span-2',
            value: (
                <div className="text-red-700">
                    <div>
                        {t('e-invoice-wrong-reason')}:{' '}
                        {orDash(eInvoice?.wrong_reason)}
                    </div>
                    <div className="text-xs">
                        {orDash(eInvoice?.marked_wrong_by_name)} ·{' '}
                        {formatEInvoiceTime(eInvoice?.marked_wrong_at)}
                    </div>
                </div>
            ),
        },
        {
            hidden: lineItems.length === 0,
            label: t('e-invoice-contracts'),
            className: 'col-span-2 md:col-span-4',
            value: contractsValue,
        },
        {
            hidden: lineItems.length === 0,
            label: t('e-invoice-item-descriptions'),
            className: 'col-span-2 md:col-span-4',
            value: linesValue,
        },
    ]

    const publishButton = canPublish && (
        <Button
            startIcon={<Send className="h-4 w-4" />}
            onClick={() => setPublishOpen(true)}
            disabled={!!publishBlockedReason}
        >
            {t(
                eInvoice?.status === 'failed'
                    ? 'e-invoice-republish'
                    : 'e-invoice-publish'
            )}
        </Button>
    )

    const footer = (
        <div className="flex flex-wrap items-center justify-end gap-2">
            {(badge === 'publishing' || badge === 'waiting-tax') && (
                <Button
                    variant="outlined"
                    startIcon={<RefreshCw className="h-4 w-4" />}
                    onClick={() => refetch()}
                    loading={isFetching}
                    disabled={isFetching}
                >
                    {t('refresh')}
                </Button>
            )}
            {eInvoice?.pdf_url && (
                <>
                    <Button
                        variant="outlined"
                        startIcon={<Eye className="h-4 w-4" />}
                        // pdf_url ép tải về → xem trong popup (tải blob rồi hiện)
                        onClick={() => setPdfOpen(true)}
                    >
                        {t('e-invoice-view-pdf')}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<Download className="h-4 w-4" />}
                        onClick={() => downloadFile((value) => value.pdf_url)}
                    >
                        {t('e-invoice-download-pdf')}
                    </Button>
                </>
            )}
            {eInvoice?.xml_url && (
                <Button
                    variant="outlined"
                    startIcon={<Download className="h-4 w-4" />}
                    onClick={() => downloadFile((value) => value.xml_url)}
                >
                    {t('e-invoice-download-xml')}
                </Button>
            )}
            {canPublish && lineItems.length > 0 && (
                <Button
                    variant="outlined"
                    startIcon={<Pencil className="h-4 w-4" />}
                    onClick={() => setEditItemsOpen(true)}
                >
                    {t('e-invoice-items-edit')}
                </Button>
            )}
            {canMarkWrong && (
                <Button
                    variant="outlined"
                    color="error"
                    startIcon={<TriangleAlert className="h-4 w-4" />}
                    onClick={() => setMarkWrongOpen(true)}
                >
                    {t('e-invoice-mark-wrong')}
                </Button>
            )}
            {publishButton &&
                (publishBlockedReason ? (
                    <Tooltip title={publishBlockedReason}>
                        <span>{publishButton}</span>
                    </Tooltip>
                ) : (
                    publishButton
                ))}
        </div>
    )

    const history = eInvoice?.history ?? []

    const renderBody = () => {
        if (isLoading)
            return (
                <div className="px-4 pb-4 text-sm text-gray-500">
                    {t('e-invoice-loading')}
                </div>
            )
        // `e-invoice-not-issued` đã được hook đổi thành null (chưa phát hành)
        if (isError)
            return (
                <div className="mx-4 mb-4 flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
                    {errorText(error)}
                    <Button variant="outlined" onClick={() => refetch()}>
                        {t('refresh')}
                    </Button>
                </div>
            )
        return null
    }

    // Đang tải / lỗi tải: chỉ hiện tiêu đề + thông báo, chưa hiện thông tin và nút
    const body = renderBody()

    return (
        <div className="rounded-sm bg-white" data-tour="invoice-e-invoice">
            <InfoGrid
                title={t('e-invoice-title')}
                icon={<FileText />}
                items={body ? [] : items}
                footer={body ? undefined : footer}
                className="bg-white"
            />
            {body}

            {history.length > 0 && (
                <div className="mx-4 mb-4 rounded-md border border-gray-200 text-sm">
                    <button
                        type="button"
                        className="flex w-full items-center justify-between px-4 py-2 font-medium text-gray-700"
                        onClick={() => setHistoryOpen((value) => !value)}
                    >
                        {t('e-invoice-history', { total: history.length })}
                        {historyOpen ? (
                            <ChevronUp className="h-4 w-4" />
                        ) : (
                            <ChevronDown className="h-4 w-4" />
                        )}
                    </button>
                    {historyOpen && (
                        <ol className="space-y-2 border-t border-gray-200 px-4 py-3">
                            {history.map((item) => (
                                <li
                                    key={item.e_invoice_id}
                                    className="flex gap-3"
                                >
                                    <span className="w-[120px] shrink-0 whitespace-nowrap text-gray-500">
                                        {formatEInvoiceTime(item.created_at)}
                                    </span>
                                    <span className="text-gray-800">
                                        {t('e-invoice-history-failed', {
                                            name: orDash(
                                                item.published_by_name
                                            ),
                                        })}
                                        <span className="ml-1 text-red-700">
                                            {orDash(item.error_message)} (
                                            {orDash(item.error_code)})
                                        </span>
                                    </span>
                                </li>
                            ))}
                        </ol>
                    )}
                </div>
            )}

            {publishOpen && (
                <PublishEInvoiceDialog
                    open
                    invoiceID={invoiceID}
                    onClose={() => setPublishOpen(false)}
                />
            )}
            {editItemsOpen && lines && (
                <EditEInvoiceItemsDialog
                    open
                    invoiceID={invoiceID}
                    lines={lines}
                    onClose={() => setEditItemsOpen(false)}
                />
            )}
            {pdfOpen && (
                <EInvoicePdfDialog
                    open
                    invoiceID={invoiceID}
                    onClose={() => setPdfOpen(false)}
                    title={[
                        t('e-invoice-title'),
                        [eInvoice?.inv_series, eInvoice?.inv_no]
                            .filter(Boolean)
                            .join(' – '),
                    ]
                        .filter(Boolean)
                        .join(' · ')}
                />
            )}
            {markWrongOpen && (
                <MarkEInvoiceWrongDialog
                    open
                    invoiceID={invoiceID}
                    onClose={() => setMarkWrongOpen(false)}
                />
            )}
        </div>
    )
}

export default EInvoiceSection
